import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
    Directive,
    ElementRef,
    computed,
    effect,
    inject,
    input,
    output,
    signal,
} from '@angular/core';
import { v4 as uuidv4 } from 'uuid';
import { CmdkService } from './cmdk.service';

@Directive({
    selector: '[qpxCmdkItem]',
    host: {
        class: 'qpx-cmdk-item',
        '(click)': 'onClick()',
        '(keydown.enter)': 'onEnter($event)',
        '(mouseup)': 'onMouseUp()',
        '[class.hidden!]': '!filtered()',
        '[attr.data-value]': 'resolvedValue()',
        '[id]': 'itemId',
        '[attr.role]': '"option"',
        '[attr.type]': '"button"',
        '[attr.aria-disabled]': 'disabled()',
        '[attr.aria-selected]': 'active()',
        '[class.active]': 'active()',
        '[class.disabled]': 'disabled()',
        '[class.filtered]': 'filtered()',
    },
})
export class QpxCmdkItem {
    //#region Dependency injection
    private readonly cmdkService = inject(CmdkService);
    private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
    //#endregion

    //#region Inputs
    public readonly disabled = input(false, {
        transform: coerceBooleanProperty,
    });
    public readonly value = input<string | undefined>();
    //#endregion

    //#region Outputs
    public readonly selected = output<void>();
    //#endregion

    //#region State
    public readonly itemId = `cmdk-item-${uuidv4()}`;
    public readonly filtered = signal(true);
    protected readonly active = signal(false);
    private previousValue = '';
    //#endregion

    //#region Derived state
    public readonly resolvedValue = computed(
        () =>
            this.value() ??
            this.elementRef.nativeElement.textContent?.trim().toLowerCase() ??
            ''
    );
    //#endregion

    //#region Lifecycle hooks
    constructor() {
        effect(() => {
            const nextValue = this.resolvedValue();
            if (nextValue === this.previousValue) return;

            this.cmdkService.itemValueChanged(this.previousValue, nextValue);
            this.previousValue = nextValue;
        });
    }
    //#endregion

    //#region Public methods
    public nativeElement(): HTMLElement {
        return this.elementRef.nativeElement;
    }

    public getLabel(): string {
        return this.resolvedValue();
    }

    public isDisabled(): boolean {
        return this.disabled();
    }

    public setActiveStyles(): void {
        this.active.set(true);
    }

    public setInactiveStyles(): void {
        this.active.set(false);
    }

    public setFiltered(value: boolean): void {
        this.filtered.set(value);
    }
    //#endregion

    //#region Template methods
    protected onClick(): void {
        if (this.disabled()) return;

        this.selected.emit();
    }

    protected onEnter(event: Event): void {
        event.preventDefault();
        event.stopPropagation();
        if (this.disabled()) return;

        this.selected.emit();
    }

    protected onMouseUp(): void {
        if (this.disabled()) return;

        this.cmdkService.itemClicked(this.resolvedValue());
    }
    //#endregion
}
